import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Combobox } from './combobox.js';
import { MultiSelect } from './multi-select.js';
import type { SelectOption } from './select.js';

/// Botão em forma de etiqueta, para escolhas curtas lado a lado.
export function Chip({
  selected,
  onClick,
  children,
  role = 'radio',
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  role?: 'radio' | 'checkbox' | 'tab';
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'tab' ? undefined : selected}
      aria-selected={role === 'tab' ? selected : undefined}
      onClick={onClick}
      className={cn(
        'min-h-9 shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-3.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
        selected
          ? 'border-brand-500 bg-brand-500/15 text-content'
          : 'border-surface-border bg-surface-card text-content-muted hover:text-content',
      )}
    >
      {children}
    </button>
  );
}

/// Compatibilidade das telas: escolha única sempre em campo com busca.
export function ChoiceChips({
  label,
  options,
  value,
  onChange,
  placeholder = 'Buscar',
  className,
}: {
  label: string;
  options: ReadonlyArray<SelectOption>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <Combobox
      label={label}
      className={className}
      clearable={false}
      placeholder={placeholder}
      selectedLabel={options.find((option) => option.value === value)?.label ?? ''}
      options={[...options]}
      onSelect={(choice) => {
        if (choice.kind === 'option') onChange(choice.value);
      }}
    />
  );
}

/// Compatibilidade das telas: várias escolhas sempre em campo com busca.
export function ToggleChips({
  label,
  options,
  selected,
  onChange,
  itemNoun,
  className,
}: {
  label: string;
  options: ReadonlyArray<SelectOption>;
  selected: readonly string[];
  onChange: (values: string[]) => void;
  /// Plural do que está sendo escolhido, para o resumo da lista: "3 serviços".
  itemNoun: string;
  className?: string | undefined;
}) {
  return (
    <MultiSelect
      label={label}
      className={className}
      options={options}
      selected={selected}
      onChange={onChange}
      itemNoun={itemNoun}
    />
  );
}
